import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * ローカル日時を指定タイムゾーンに変換
 */
export const convertLocalToTimezone = (
  dateStr: string,
  timeStr: string,
  targetTimeZone: string,
): string => {
  try {
    const localDateTime = dayjs(`${dateStr} ${timeStr}`);
    const targetTime = localDateTime.tz(targetTimeZone);
    return targetTime.format("YYYY-MM-DD HH:mm:ss");
  } catch (error) {
    console.warn("Error converting local to timezone with dayjs:", error);
    return `${dateStr} ${timeStr}:00`;
  }
};

/**
 * 指定タイムゾーンの日時をローカルタイムゾーンに変換
 */
export const convertTimezoneToLocal = (
  dateTimeStr: string,
  sourceTimeZone: string,
): { date: string; time: string } => {
  try {
    const sourceTime = dayjs.tz(dateTimeStr, sourceTimeZone);
    const localTime = sourceTime.local();

    return {
      date: localTime.format("YYYY-MM-DD"),
      time: localTime.format("HH:mm"),
    };
  } catch (error) {
    console.warn("Error converting timezone to local with dayjs:", error);
    return {
      date: dateTimeStr.split(" ")[0] || "",
      time: dateTimeStr.split(" ")[1]?.slice(0, 5) || "",
    };
  }
};

/**
 * 指定タイムゾーン間で日時を変換する
 */
export const convertTimezoneToTimezone = (
  dateStr: string,
  timeStr: string,
  sourceTimeZone: string,
  targetTimeZone: string,
): { date: string; time: string } => {
  try {
    const sourceTime = dayjs.tz(`${dateStr} ${timeStr}`, sourceTimeZone);
    const targetTime = sourceTime.tz(targetTimeZone);
    return {
      date: targetTime.format("YYYY-MM-DD"),
      time: targetTime.format("HH:mm"),
    };
  } catch (error) {
    console.warn("Error converting timezone to timezone with dayjs:", error);
    return {
      date: dateStr,
      time: timeStr,
    };
  }
};

/**
 * 現在時刻をローカルタイムゾーンで取得
 */
export const getCurrentTimeLocal = (): string => {
  return dayjs().format("HH:mm");
};

/**
 * 時間をHH:MM形式にフォーマット
 */
export const formatTimeLocal = (date: Date): string => {
  return dayjs(date).format("HH:mm");
};

/**
 * 日時をYYYY/MM/DD HH:mm形式にフォーマット
 */
export const formatTimestamp = (dateString: string) => {
  return dayjs(dateString).format("YYYY/MM/DD HH:mm");
};

/**
 * 今日の日付かどうかを判定
 */
export const isToday = (year: number, month: number, day: number): boolean => {
  const today = new Date();
  return (
    today.getFullYear() === year &&
    today.getMonth() === month &&
    today.getDate() === day
  );
};

/**
 * Format the timestamp to a relative time (e.g., "2 days ago")
 */
export const formatRelativeTimestamp = (timestamp: string) => {
  try {
    return dayjs(timestamp).fromNow();
  } catch (_e) {
    return timestamp;
  }
};

/**
 * ブラウザのタイムゾーンからUTCに日付を変換する
 */
export const convertDateToUTC = (dateString: string): string => {
  if (!dateString) return "";
  return dayjs(dateString).utc().format("YYYY-MM-DD");
};

export const parseTimeValue = (
  value?: string,
): { hour: number | null; minute: number | null } => {
  if (!value) return { hour: null, minute: null };
  const match = value.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return { hour: null, minute: null };
  return { hour: Number(match[1]), minute: Number(match[2]) };
};

export const formatTimeValue = (hour: number, minute: number): string => {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};
